import { useState, useEffect, useCallback, useMemo } from 'react';
import { reportService } from 'api/services/reportService';
import type {
  EmployeeActivityReportRequest, EmployeeActivityReportRow, EmployeeActivitySummary,
  WipReportRequest, WipReportRow, WipReportSummary,
  TimesheetStatusReportRequest, TimesheetStatusReportRow,
} from 'api/types/report.types';
import type { PaginatedResponse } from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

// ---- Employee Activity Report ----

export const useEmployeeActivityReport = (params?: EmployeeActivityReportRequest) => {
  const [data, setData] = useState<EmployeeActivityReportRow[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<EmployeeActivityReportRow>['pagination']>(EMPTY_PAGINATION);
  const [summary, setSummary] = useState<EmployeeActivitySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [listRes, summaryRes] = await Promise.all([
        reportService.getEmployeeActivity(params),
        reportService.getEmployeeActivitySummary(params),
      ]);
      setData(listRes.data); setPagination(listRes.pagination);
      setSummary(summaryRes);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, pagination, summary, loading, error, refetch: fetchData };
};

// ---- WIP Report ----

export const useWipReport = (params?: WipReportRequest) => {
  const [data, setData] = useState<WipReportRow[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<WipReportRow>['pagination']>(EMPTY_PAGINATION);
  const [summary, setSummary] = useState<WipReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [listRes, summaryRes] = await Promise.all([
        reportService.getWip(params),
        reportService.getWipSummary(params),
      ]);
      setData(listRes.data); setPagination(listRes.pagination);
      setSummary(summaryRes);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, pagination, summary, loading, error, refetch: fetchData };
};

// ---- Timesheet Status Report ----

export const useTimesheetStatusReport = (params?: TimesheetStatusReportRequest) => {
  const [data, setData] = useState<TimesheetStatusReportRow[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<TimesheetStatusReportRow>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await reportService.getTimesheetStatus(params);
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, pagination, loading, error, refetch: fetchData };
};
